import { faPlusCircle } from '@fortawesome/pro-light-svg-icons/faPlusCircle';
import { connect } from 'formik';
import React, { Component, PropsWithChildren } from 'react';
import { DragDropContext as _DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { FormGroup } from '../FormGroup';
import { Icon } from '../Icon';
import { FieldHelpersWrapper } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import './ListBuilder.scss';
import ListRow from './ListRow';
import { IListBuilderProps, IListItemType, InnerListBuilderProps } from './types';
import { generateItem } from './utils';

const DragDropContext = _DragDropContext as unknown as React.AsFCC<typeof _DragDropContext>;

/**
 * Sortable dynamic list of inputs.
 *
 * From the consumer's point of view should be considered as a form element-component with main props:
 * "name", "value", "onChange". Where "value" is an array of objects {id, value, isNew?}.
 *
 * To sort, use Drag&Drop, or Shift + Up/Down arrows.
 */
export class ListBuilder extends Component<PropsWithChildren<InnerListBuilderProps>, {}> {
  private listId = createId('list');

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);

    const { value = [] } = resolveProps(this.props, this.props.formik);
    if (this.props.min && value.length < this.props.min) {
      const newItems = Array.from(new Array(this.props.min - value.length), () => {
        return generateItem();
      });
      setTimeout(() => {
        this.handleChange(value.concat(newItems));
      }, 0);
    }
  }
  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
  }

  public handleChange(value: IListItemType[]) {
    if (this.props.onChange) {
      this.props.onChange(this.props.name, value);
      return;
    }
    if (this.props.formik && this.props.formik.setFieldValue) {
      this.props.formik.setFieldValue(this.props.name, value);
    }
  }

  public addItem = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { value = [] } = resolveProps(this.props, this.props.formik);
    if (this.props.max && value.length >= this.props.max) {
      return;
    }
    this.handleChange(value.concat([generateItem()]));
  };
  public deleteItem(item: IListItemType) {
    const { value = [] } = resolveProps(this.props, this.props.formik);
    const { index } = this.findItem(item.id);
    value.splice(index, 1);
    this.handleChange(value);
  }
  public changeItem(key: number, inputValue: string) {
    const { value = [] } = resolveProps(this.props, this.props.formik);
    const items = value.slice();
    items[key] = { ...items[key], value: inputValue };
    this.handleChange(items);
  }
  public moveItem = (destinationId: number, draggableId: string, sourceId: number) => {
    const { value = [] } = resolveProps(this.props, this.props.formik);
    const items = value.slice();
    const { index } = this.findItem(draggableId);
    const movingItem = items[index];
    items.splice(sourceId, 1);
    items.splice(destinationId, 0, movingItem);
    this.handleChange(items);
  };
  public findItem(id: string) {
    const { value = [] } = resolveProps(this.props, this.props.formik);
    const item = value.filter((c: IListItemType) => {
      return !isNaN(parseInt(c.id, 10)) ? parseInt(c.id, 10) === parseInt(id, 10) : c.id === id;
    })[0];
    return {
      index: value.indexOf(item),
      item,
    };
  }
  public handleDragEnd = (result: DropResult) => {
    const { source, draggableId } = result;
    if (!result.destination) {
      return;
    }
    if (result.destination.droppableId === source.droppableId && result.destination.index === source.index) {
      return;
    }
    this.moveItem(result.destination.index, draggableId, result.source.index);
  };

  public render() {
    const { min = 0, max, reorderable } = this.props;
    const { value = [], touched = false, required } = resolveProps(this.props, this.props.formik);
    const hideDelete = value.length <= min ? true : false;

    const listRows = value.map((item: IListItemType, index: number) => {
      const invalidRow = required && touched && item.value.trim() === '';
      return (
        <ListRow
          value={item.value}
          name={this.props.name}
          key={item.id}
          id={item.id}
          index={index}
          hideDelete={hideDelete}
          onDeleteClick={() => this.deleteItem(item)}
          onChange={(inputName, inputValue) => this.changeItem(index, inputValue)}
          isValid={!invalidRow}
          reorderable={reorderable}
        />
      );
    });
    const disableAddBtn = max && value.length >= max ? { disabled: 'disabled' } : {};

    const list = (
      <div className={'hlc-list-builder'} qa-id={this.props['qa-id']}>
        <FieldHelpersWrapper id={this.listId} props={this.props} useGroupLabel={true}>
          {listRows}
          <FormGroup>
            <a href="#" role="button" {...disableAddBtn} onClick={this.addItem}>
              <Icon icon={faPlusCircle} size={'sm'} /> Add
            </a>
          </FormGroup>
        </FieldHelpersWrapper>
      </div>
    );

    return reorderable ? (
      <DragDropContext onDragEnd={this.handleDragEnd}>
        <Droppable droppableId={this.listId}>
          {(provided) => (
            <div ref={provided.innerRef}>
              {list}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    ) : (
      list
    );
  }
}

/**
 * Represents an interactive control that allows to create and edit a list of options.
 * The data structure for the "value" prop is an array of objects, where each object has id and value keys.
 *
 * It builds a sortable list of inputs, each input corresponds to an object in the "value" array.
 * Should be used as a form element with only two required props:
 * **value** and **onChange**.
 *
 * **Reorder**: Use drag-n-drop to reorder items. Or Shift + (ArrowUp / ArrowDown) on focused item row to move
 * it up/down.
 *
 * **Validation**: Use inside of the Form component for validation.
 * Pass *rules={required: "All options are required"}* prop to the ListBuilder if you don't want to allow empty items.
 * ListBuilder will automatically run validation when inside the form.
 */
export default connect<IListBuilderProps>(ListBuilder);
